import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { Backdrop, IconButton, Button, TextField, Box } from '@mui/material';
import i18next from 'i18next';
import { DATE_ABBR_TO_INTL_FORMAT, formatDateToOpenSearch, getDateRangerOptions, isSameYMD } from './DateRanger.utils';
import styles from './DateRangerFilter.module.scss';

type DateRangerFilterComponent = {
  id: string;
  onFilter: (filter: { value: [string, string] | null } | null) => void;
  filter: { value: [string, string] } | null;
  statuses: string[];
  isSubCr?: boolean;
  classes?: string;
};

function formatDate(date: Date | null, lang: string): string {
  const options = { month: '2-digit' as const, day: '2-digit' as const, year: 'numeric' as const };
  const langINTL = DATE_ABBR_TO_INTL_FORMAT[lang as keyof typeof DATE_ABBR_TO_INTL_FORMAT] || 'en-GB';

  return date ? date.toLocaleDateString(langINTL, options) : '--/--/----';
}

const DateRangerFilter: React.FC<DateRangerFilterComponent> = (props) => {
  const { id, onFilter, filter } = props;
  const { t } = useTranslation();
  const lang = i18next.language.toUpperCase();
  const [selectFiltersOpen, setSelectFiltersOpen] = useState(false);

  const dateRangerOptions = useMemo(() => getDateRangerOptions(id, t), [id, t]);

  const removeSelectedOptionClass = useCallback(() => {
    dateRangerOptions.forEach((option) => {
      const optionElement = document.getElementById(option.id);
      if (optionElement?.classList.contains('Mui-selected')) optionElement.classList.remove('Mui-selected');
    });
  }, [dateRangerOptions]);

  const setSelectedOptionClass = useCallback((optionId: string) => {
    removeSelectedOptionClass();
    document.getElementById(optionId)?.classList.add('Mui-selected');
  }, [removeSelectedOptionClass]);

  const searchOptionByDates = useCallback((startDate: Date, endDate: Date) => {
    return dateRangerOptions.find((option) => {
      const [optStart, optEnd] = option.getValue({ isValid: () => true });

      return (
        optStart && optEnd && isSameYMD(optStart, startDate) && isSameYMD(optEnd, endDate));
    });
  }, [dateRangerOptions]);

  const handleApply = () => {
    if (localStartDate && localEndDate) {
      setSelectFiltersOpen(false);
      const startDateObj = new Date(localStartDate);
      const endDateObj = new Date(localEndDate);
      const currentOption = searchOptionByDates(startDateObj, endDateObj);

      onFilter({ value: [formatDateToOpenSearch(startDateObj, true), formatDateToOpenSearch(endDateObj, false)] });
      setDateRange({
        startDate: startDateObj,
        endDate: endDateObj,
        label: currentOption ? currentOption.label : t('dateRangeFilter.custom'),
      });

      if (currentOption) setSelectedOptionClass(currentOption.id);
    } else if (!localStartDate && !localEndDate) {
      setSelectFiltersOpen(false);
      onFilter(null);
      setDateRange({
        startDate: null,
        endDate: null,
        label: 'None',
      });
      removeSelectedOptionClass();
      setSelectedOptionClass(`${id}-reset-option`);
    }
  };

  useEffect(() => {
    removeSelectedOptionClass();

    if (filter && filter?.value?.length > 0) {
      const [startDate, endDate] = filter.value;
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const currentOption = searchOptionByDates(startDateObj, endDateObj);

      setDateRange({
        startDate: startDateObj,
        endDate: endDateObj,
        label: currentOption ? currentOption.label : t('dateRangeFilter.custom'),
      });
      setLocalStartDate(startDateObj.toISOString().slice(0, 10));
      setLocalEndDate(endDateObj.toISOString().slice(0, 10));

      if (currentOption) setSelectedOptionClass(currentOption.id);
    } else {
      setDateRange({
        startDate: null,
        endDate: null,
        label: 'None',
      });
      setLocalStartDate('');
      setLocalEndDate('');
      setSelectedOptionClass(`${id}-reset-option`);
    }
  }, [filter, id, t, removeSelectedOptionClass, searchOptionByDates, setSelectedOptionClass]);

  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null; label: string }>({
    startDate: null,
    endDate: null,
    label: 'None',
  });

  const [localStartDate, setLocalStartDate] = useState<string>('');
  const [localEndDate, setLocalEndDate] = useState<string>('');

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        className={styles.DateRangerButton}
        onClick={() => setSelectFiltersOpen(!selectFiltersOpen)}
        endIcon={<DateRangeIcon />}
      >
        <div>
          {selectFiltersOpen
            ? <span className={styles.ButtonLabel}>{t('dateRangeFilter.selectingARange')}</span>
            : (
              <span className={styles.DatesLabels}>
                <b>
                  { dateRange?.startDate
                    ? t('dateRangeFilter.currentRange', { rangeLabel: dateRange?.label ? dateRange.label : t('dateRangeFilter.custom') })
                    : <span className={styles.ButtonLabel}>{t('dateRangeFilter.selectADateRange') }</span>}
                </b>
                <br />
                {dateRange?.startDate && <span>{t('dateRangeFilter.from')} <u>{formatDate(dateRange?.startDate, lang)}</u> {t('dateRangeFilter.to')} <u>{formatDate(dateRange?.endDate, lang)}</u></span>}
              </span>
            )}
        </div>
      </Button>
      <Backdrop open={selectFiltersOpen} className={styles.BackdropFilter}>
        <div className={styles.DateRangerPickerContainer} id="date-range-picker-container">
          <div className={styles.DateRangerPickerHeader}>
            <h3>{t('dateRangeFilter.selectADateRange')}</h3>
            <IconButton onClick={() => setSelectFiltersOpen(false)}><CloseIcon /></IconButton>
          </div>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                type="date"
                label={t('dateRangeFilter.from')}
                value={localStartDate}
                onChange={(e) => setLocalStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <span>–</span>
              <TextField
                type="date"
                label={t('dateRangeFilter.to')}
                value={localEndDate}
                onChange={(e) => setLocalEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Box>
            <Button variant="contained" color="primary" onClick={handleApply}>
              {t('common.apply')}
            </Button>
          </Box>
        </div>
      </Backdrop>
    </>
  );
};

export default DateRangerFilter;
